import React from "react";
import { useNavigate, useParams } from "react-router-dom";

// components
import DataDisplay from "../../../component/dataDisplay";
import Button from "../../../component/element/forms/Button";
import UploadDocument from "../misc/UploadDocument";
import DocumentList from "../../../component/DocumentList";
// import CreateUser from "../../users/create";

// constants
import { VscFolderActive } from "react-icons/vsc";
import { FaFilePdf, FaArrowLeftLong, FaStar } from "react-icons/fa6";
// import office from "../../../assets/images/user/office.png";

// state
import { get_company, delete_company } from "../../../services/apis/companies";
import { useAppDispatch, useAppSelector } from "../../../store";
// import CompanyStaff from "../misc/CompanyStaff";
import CompanyBranch from "../misc/CompanyBranch";
import CompanyBranchUser from "../misc/CompanyBranchUsers";
import { LuGlobe } from "react-icons/lu";
import SimpleTab, { TItem } from "../../../component/ui/tabs/SimpleTab";
import AddUser from "../add/AddUser";
import { FaPlus } from "react-icons/fa";

const CompanyDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const companyId = Number(id);
  const dispatch = useAppDispatch();
  const [focused, focusedSet] = React.useState<TItem>({
    id: "info",
    name: "Info",
  });

  const loadingCompanyId = useAppSelector(
    ({ companies }) => companies.loadingCompanyId,
  );
  const loading = useAppSelector(({ companies }) => companies.loading);

  const [tab, tabSet] = React.useState<TItem>({
    id: "branches",
    name: "Branches",
  });
  const [visible, setVisible] = React.useState(false);
  const [upload, uploadSet] = React.useState<boolean>(false);

  // Delete company handler
  const handleDeleteCompany = async () => {
    if (!Number.isInteger(companyId) || companyId <= 0) return;
    if (await delete_company(companyId, dispatch)) navigate(-1);
  };

  const company = useAppSelector(({ companies }) => companies.company);

  const onCloseModal = () => {
    setVisible(false);
  };

  const onLoad = React.useCallback(() => {
    if (Number.isInteger(companyId) && companyId > 0) {
      get_company(companyId, dispatch);
    }
  }, [companyId, dispatch]);

  React.useEffect(() => {
    onLoad();
  }, [onLoad]);

  return (
    <div className="w-full">
      <div className="w-full flex justify-between items-center mb-6">
        <h2 className="font-bold text-xl text-gray-800 capitalize mb-0">
          Company
        </h2>

        <div className="flex items-center space-x-2">
          <Button
            title="Upload"
            children={<FaFilePdf className="mr-2" />}
            onClick={() => uploadSet(true)}
            className="px-4"
          />
          <Button
            title="Back"
            children={<FaArrowLeftLong className="mr-2" />}
            onClick={() => navigate(-1)}
          />
          {/* Delete Company Button - visible to admins only, add role check if needed */}
          <Button
            loading={loadingCompanyId === companyId}
            title="Delete Company"
            className="px-4 bg-red-600 text-white hover:bg-red-700"
            onClick={handleDeleteCompany}
          />
        </div>
        <UploadDocument visible={upload} onHandler={() => uploadSet(false)} />
      </div>

      <div className="w-full grid lg:grid-cols-3 gap-6 mb-12">
        {/* Banner */}
        <div className="relative w-full h-[350px] bg-gradient-to-br from-primary-800 to-primary-500 flex-col items-center text-center p-6 rounded-xl shadow-sm overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
          {/* Title and Status */}
          <div className="absolute bottom-4 left-4 right-4">
            {/* Display company's name */}
            <h2 className="text-3xl font-bold text-white capitalize">
              {company?.name || (loading ? "Loading company..." : "Company")}
            </h2>
          </div>

          {company && (
            <div className="absolute top-3 right-3 flex items-center justify-center mb-4 bg-customgreen px-3 py-1 rounded-full">
              <DataDisplay
                detail={
                  <div
                    className={`flex items-center ${company.is_active ? "text-primary-600" : "text-gray-500"}`}
                  >
                    <VscFolderActive size={18} />
                    <span className="ml-2 font-medium">
                      {company.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                }
              />
            </div>
          )}
        </div>

        <div className="w-full lg:col-span-2 bg-white p-6 rounded-xl shadow-sm overflow-hidden">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-semibold text-xl text-gray-800 whitespace-nowrap">
              Company Details
            </h3>
            <SimpleTab
              variant="small"
              items={[
                { id: "info", name: "Info" },
                { id: "files", name: "Files" },
              ]}
              focused={focused}
              focusedClassName="bg-white text-black"
              onClick={focusedSet}
            />
          </div>
          {focused.id === "info" && (
            <div className="grid md:grid-cols-2 gap-3">
              {/* Display company's RC number */}
              <DataDisplay
                title="RC Number"
                detail={
                  <span className="uppercase">{company?.rc_number || "—"}</span>
                }
              />

              {/* Display company's email */}
              <DataDisplay title="Email" detail={company?.email || "—"} />

              {/* Display company's mobile number */}
              <DataDisplay
                title="Mobile Number"
                detail={company?.phone || "—"}
              />

              {/* Display company's premium status */}
              <DataDisplay
                title="Premium Status"
                detail={
                  <div
                    className={`flex items-center space-x-2 ${company?.is_elite ? "text-yellow-400" : "text-gray-300"}`}
                  >
                    <FaStar />
                    <FaStar />
                    <FaStar />
                  </div>
                }
              />
              {/* Display company's website */}
              <DataDisplay
                title="Website"
                icon={<LuGlobe />}
                detail={
                  company?.website ? (
                    <a
                      className="cursor-pointer"
                      href={company.website}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {company.website}
                    </a>
                  ) : (
                    "—"
                  )
                }
              />
              <DataDisplay title="Address" detail={company?.address || "—"} />
            </div>
          )}
          {focused.id === "files" && (
            <div className="max-h-[500px] lg:max-h-[230px] overflow-y-auto">
              <DocumentList />
            </div>
          )}
        </div>
      </div>

      <div>
        <div>
          <div className="mb-4 flex justify-between">
            <div>
              <SimpleTab
                variant="small"
                items={[
                  { id: "branches", name: "Branches" },
                  { id: "employees", name: "Employees" },
                ]}
                focused={tab}
                focusedClassName="bg-white text-black "
                onClick={tabSet}
              />
            </div>

            {tab.id === "employees" && (
              <Button
                children={<FaPlus className="mr-2" />}
                className="px-4 flex items-center whitespace-nowrap"
                onClick={() => setVisible(true)}
                title="Add Employees"
              />
            )}
          </div>

          <div className="mt-3">
            {tab.id === "branches" && <CompanyBranch companyId={companyId} />}
            {tab.id === "employees" && (
              <CompanyBranchUser companyId={companyId} />
            )}
          </div>
        </div>

        {Number.isInteger(companyId) && companyId > 0 && (
          <AddUser
            show={visible}
            isClose={onCloseModal}
            companyId={companyId}
          />
        )}
      </div>
    </div>
  );
};

export default CompanyDetails;
